import { useState, useEffect, useRef } from 'react';

function useAnimatedCounter(target: number, duration = 1000) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number>();
  useEffect(() => {
    const start = performance.now();
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      setValue(Math.round(target * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);
  return value;
}

export function AnimatedStatCard({ icon, label, value, suffix, color, delay }: {
  icon: React.ReactNode; label: string; value: number; suffix?: string; color: string; delay: number;
}) {
  const displayed = useAnimatedCounter(value);
  return (
    <div className="glass-card stat-card animate-float-in" style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
        <span className="stat-card__label" style={{ margin: 0 }}>{label}</span>
        <span style={{ fontSize: '20px', color, filter: `drop-shadow(0 0 6px ${color})` }}>{icon}</span>
      </div>
      <div className="stat-card__value" style={{ color, textShadow: `0 0 24px ${color}55` }}>
        {displayed}{suffix || ''}
      </div>
    </div>
  );
}

export function StatCard({ label, value, icon, accent }: { label: string; value: string | number; icon: React.ReactNode; accent?: boolean }) {
  return (
    <div className="glass-card stat-card">
      <div className="stat-card__label">{icon} {label}</div>
      <div className={`stat-card__value ${accent ? 'stat-card__accent' : ''}`}>{value}</div>
    </div>
  );
}

export function StarRating({ rating, onChange }: { rating: number; onChange: (r: number) => void }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`star-rating__star ${star <= (hover || rating) ? 'star-rating__star--filled' : ''}`}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(star)}
          onTouchStart={(e) => {
            e.preventDefault();
            setHover(star);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            onChange(star);
            setHover(0);
          }}
          role="button"
          aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
        >
          ★
        </span>
      ))}
    </div>
  );
}

export function TargetCard({ num, label, goal, met, remaining }: { num: number; label: string; goal: number; met: boolean; remaining: number }) {
  return (
    <div className="glass-card" style={{ padding: 'var(--space-4)' }}>
      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
        TARGET {num}
      </div>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
        {label}: <strong style={{ color: 'var(--text-primary)' }}>{goal}</strong>
      </div>
      <div className={`target-indicator target-indicator--${met ? 'on' : 'off'}`}>
        {met ? '✅ On Target' : `❌ Off Target — ${remaining} remaining`}
      </div>
    </div>
  );
}
